"use client";

import { NavbarIcon } from "@/assets/icons/MainPageIcon";
import { MenuItem } from "@/lib/types";
import { useLenis } from "lenis/react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { ServicesData } from "@/data/ServicesData";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

interface NavItemProps {
  item: MenuItem;
  onNavigate?: () => void;
  scrolled?: boolean;
  onHover?: () => void;
  isOverlay?: boolean;
}

const NavMenuItem = ({
  item,
  onNavigate,
  scrolled,
  onHover,
  isOverlay,
}: NavItemProps) => {
  const pathname = usePathname();
  const lenis = useLenis();
  const [isExpanded, setIsExpanded] = useState(false);
  const isActive = pathname === item.href;
  const isServices = item.label === "Services";

  const handleMobileToggle = (e: React.MouseEvent) => {
    if ((window.innerWidth < 1024 || isOverlay) && isServices) {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    } else {
      const isAnchor = item.href.includes("#");
      const targetId = isAnchor ? item.href.split("#")[1] : null;
      if (isAnchor && targetId && lenis && pathname === "/") {
        e.preventDefault();
        lenis.scrollTo(`#${targetId}`, {
          duration: 1.5,
          offset: window.innerWidth >= 1024 ? 300 : 0,
          immediate: false,
          lock: true,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        });
      }
      if (onNavigate) {
        onNavigate();
      }
    }
  };

  return (
    <li onMouseEnter={onHover}>
      <div
        onClick={handleMobileToggle}
        className={`flex items-center gap-6 cursor-pointer group`}
      >
        <Link
          href={item.href}
          className={`lg:nav-link hover:text-primary flex items-center gap-2 px-2 lg:py-2 py-1 lg:mr-2 rounded-xs transition-all duration-300 uppercase  
            ${
              isOverlay
                ? "text-xl md:text-2xl lg:text-4xl text-white font-bold mb-4"
                : `text-2xl lg:text-sm ${
                    isActive
                      ? "text-[#99b81f] font-semibold"
                      : scrolled
                        ? "lg:text-xs text-white"
                        : "lg:text-accent-foreground text-white"
                  }`
            } ${isActive && !isOverlay ? "text-[#99b81f] font-semibold" : ""}`}
        >
          <NavbarIcon size={isOverlay ? 24 : 14} />
          <span>{item.label}</span>
        </Link>

        {/* Mobile Accordion Icon */}
        {isServices && (
          <div className={`${isOverlay ? "" : "lg:hidden"} text-white p-2`}>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        )}
      </div>

      {/*  Accordion Content */}
      <AnimatePresence>
        {isServices && isExpanded && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className={`${isOverlay ? "" : "lg:hidden"} flex flex-col gap-4 pl-6 overflow-hidden py-4`}
          >
            {ServicesData.slice(0, 4).map((service, index) => (
              <motion.li
                key={service.id}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  href={`/services/${service.slug}`}
                  onClick={onNavigate}
                  className="flex items-center gap-4 text-white/70 hover:text-primary transition-colors"
                >
                  <span className="text-md text-white/70">0{index + 1}</span>
                  <span className="text-md uppercase font-black">
                    {service.title}
                  </span>
                </Link>
              </motion.li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
};

export default NavMenuItem;
